import { FilterOperator, OrderType, useSearchQSetQuestionsQuery } from '@exam-management/graphql';
import {
  useGetQSetQuestionsWithImage,
  type QSetQuestionWithQuestion,
} from '@exam-management/qset-management';
import { NetworkStatus } from '@shared/apollo';
import { usePrevious } from '@shared/hooks';
import { uniqueBy } from '@shared/utils';
import { useCallback, useState } from 'react';

import {
  concatWithUniqueQSetQuestions,
  findDifferenceQSetQuestions,
  replaceQSetQuestionsWithNewQuestions,
} from '../../utils';

interface GetInfiniteQSetQuestionDetailsListVariable {
  qSetId: string;
  pageSize: number;
}
export function useGetInfiniteQSetQuestionDetailsList(
  variable: Readonly<GetInfiniteQSetQuestionDetailsListVariable>,
) {
  const { qSetId, pageSize } = variable;

  const { getQSetQuestionsWithImage, loading: isGettingQSetQuestionWithImage } =
    useGetQSetQuestionsWithImage();

  const [qSetQuestions, setQSetQuestions] = useState<QSetQuestionWithQuestion[]>([]);
  const previousQSetQuestions = usePrevious(qSetQuestions);

  // NOTE: We need to set the page from the state instead of the url params
  // because we need to change the page without changing the url params
  const [page, setPage] = useState(0);
  // NOTE: since we setState after setQSetQuestionId, we can't use loading from Apollo, because it'll be false before finish setState
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(true);

  const {
    data,
    fetchMore: fetchMoreQSetQuestions,
    networkStatus,
    refetch: refetchQSetQuestions,
    loading: isSearchingQSetQuestions,
  } = useSearchQSetQuestionsQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        paging: {
          currentPage: 0,
          pageSize: pageSize,
        },
        order: {
          field: 'order',
          type: OrderType.Asc,
        },
        filter: {
          conditions: [
            {
              field: 'qSetId',
              value: qSetId,
              operator: FilterOperator.Equal,
            },
          ],
        },
      },
    },
    onCompleted: (data) => {
      (async () => {
        const qSetQuestionDiffs = findDifferenceQSetQuestions(
          data.excSearchQSetQuestions.qSetQuestions ?? [],
          previousQSetQuestions ?? [],
        );
        const qSetQuestionDiffsWithImage = await getQSetQuestionsWithImage(qSetQuestionDiffs);

        // Add the new questions to the list
        setQSetQuestions((prevQSetQuestions) => {
          const updatedQSetQuestions = replaceQSetQuestionsWithNewQuestions(
            prevQSetQuestions,
            qSetQuestionDiffsWithImage,
          );
          const allNewQSetQuestions = concatWithUniqueQSetQuestions(
            updatedQSetQuestions,
            qSetQuestionDiffsWithImage,
          );
          return uniqueBy(
            allNewQSetQuestions.filter((qSetQuestion): qSetQuestion is QSetQuestionWithQuestion => {
              return (
                !!qSetQuestion.question && qSetQuestion.question.questionGroup?.type !== undefined
              );
            }),
            'id',
          );
        });
        setIsQuestionsLoading(false);
      })();
    },
    notifyOnNetworkStatusChange: true,
  });

  const fetchMore = useCallback(async () => {
    const nextPage = page + 1;
    await fetchMoreQSetQuestions({
      variables: {
        input: {
          order: {
            field: 'order',
            type: OrderType.Asc,
          },
          filter: {
            conditions: [
              {
                field: 'qSetId',
                value: qSetId,
                operator: FilterOperator.Equal,
              },
            ],
          },
          paging: {
            pageSize: pageSize,
            currentPage: nextPage,
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        const uniqueQuestions = Array.from(
          new Map(
            [
              ...previousResult.excSearchQSetQuestions.qSetQuestions,
              ...fetchMoreResult.excSearchQSetQuestions.qSetQuestions,
            ].map((q) => [q.id, q]),
          ),
        ).map(([, question]) => question);
        setPage((prev) => prev + 1);
        return {
          excSearchQSetQuestions: {
            ...previousResult.excSearchQSetQuestions,
            qSetQuestions: Array.from(uniqueQuestions),
          },
        };
      },
    });
  }, [fetchMoreQSetQuestions, page, pageSize, qSetId]);

  const refetch = async () => {
    setIsQuestionsLoading(true);
    await refetchQSetQuestions();
  };

  const hasMore = (data?.excSearchQSetQuestions?.total ?? 0) > (qSetQuestions.length ?? 0);

  const isLoading = [
    NetworkStatus.loading,
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
  ].includes(networkStatus);
  const isFetchingMore = NetworkStatus.fetchMore === networkStatus;

  return {
    data: qSetQuestions,
    loading: isLoading || isQuestionsLoading,
    fetchMoreLoading:
      isFetchingMore || (isGettingQSetQuestionWithImage && isSearchingQSetQuestions),
    refetch,
    fetchMore,
    hasMore,
  };
}
